import { useQuery } from "react-query";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { loggedInApiCall } from "src/api/axiosClient/userApi";

import { z } from "zod";
import { Scores, ScoresSchema } from "../post/scoreSchema";
import { CompanyType } from "./useCreateCompany";
import { ScoreQuery } from "src/context/EntityBasedScore/useEntityBasedScoreParams";

export const getOneCompanyKey = "getOneCompany" as const;

type Options = {
    companyId: string | number | undefined;
    scoreQuery?: ScoreQuery;
    statistics?: boolean;
};

export const useGetOneCompany = (options: Options) => {
    const { companyId, scoreQuery, statistics } = options;
    const { variations, dimensions, campaign } = scoreQuery || {};

    const query = useQuery({
        queryKey: [getOneCompanyKey, companyId, variations, dimensions, campaign],
        cacheTime: 0,
        enabled: companyId !== undefined,
        queryFn: async () => {
            const url = `/api/companies/${companyId}`;
            const response = await loggedInApiCall.get(url, {
                params: {
                    variations,
                    dimensions,
                    campaign,
                    statistics,
                },
            });

            return response;
        },
        select: (request) => {
            const company = request.data;
            const data = zodParseWithLog(company, CompanyZodSchema, getOneCompanyKey);
            return data;
        },
    });

    return {
        company: query.data,
        ...query,
    };
};

export const ContributorZodSchema: z.ZodSchema<Contributor> = z.object({
    "@id": z.string(),
    id: z.number(),
    email: z.string(),
    firstname: z.string(),
    lastname: z.string(),
});

export type Contributor = {
    "@id": IRI;
    id: number;
    email: string;
    firstname: string;
    lastname: string;
};

export const CompanySectorZodSchema: z.ZodSchema<CompanySector> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    ssqId: z.number().optional(),
});

type CompanySector = {
    "@id": string;
    id: number;
    name?: string;
    ssqId?: number;
};

const IngredientLineZodSchema: z.ZodSchema<IngredientSupplierIngredient> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
});

export type IngredientLine = {
    "@id": IRI;
    id: number;
    name: string;
};

const IngredientSupplierIngredientZodSchema: z.ZodSchema<IngredientSupplierIngredient> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    ingredientLine: IngredientLineZodSchema.optional(),
    ingredientReferenceId: z.number().optional(),
    origin: z.string().optional(),
});

export type IngredientSupplierIngredient = {
    "@id": IRI;
    id: number;
    name: string;
    ingredientLine?: IngredientLine;
    ingredientReferenceId?: number;
    origin?: string;
};

const IngredientSupplierZodSchema: z.ZodSchema<IngredientSupplier> = z.object({
    "@id": z.string(),
    id: z.number(),
    ingredient: IngredientSupplierIngredientZodSchema,
});

type IngredientSupplier = {
    "@id": IRI;
    id: number;
    ingredient: IngredientSupplierIngredient;
};

const CompanyBaseZodSchema = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    type: z.enum(["main", "branch", "provider"]),
    companySector: CompanySectorZodSchema.optional(),
    ingredientSuppliers: IngredientSupplierZodSchema.array().optional(),
    clientId: z.string().optional(),
    address1: z.string().optional(),
    address2: z.string().optional(),
    zipcode: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    country: z.string().optional(),
    phone: z.string().optional(),
    email: z.string().optional(),
    users: ContributorZodSchema.array().optional(),

    scores: ScoresSchema.optional(),

    scoresAvg: ScoresSchema.optional(),
    scoresMax: ScoresSchema.optional(),
    scoresMin: ScoresSchema.optional(),
    scoresCount: ScoresSchema.optional(),

    prevScores: ScoresSchema.optional(),
    prevCampaignName: z.string().optional(),

    canView: z.boolean(),
    canModify: z.boolean(),
    canDelete: z.boolean(),
});

const CompanyZodSchema: z.ZodType<Company> = CompanyBaseZodSchema.extend({
    parents: z.array(CompanyBaseZodSchema).optional(),
});

export type Company = {
    "@id": IRI;
    id: number;
    name: string;
    companySector?: CompanySector;
    type: CompanyType;
    ingredientSuppliers?: IngredientSupplier[];
    clientId?: string;
    address1?: string;
    address2?: string;
    zipcode?: string;
    city?: string;
    state?: string;
    country?: string;
    phone?: string;
    email?: string;
    parents?: Company[];
    users?: Contributor[];
    canView: boolean;
    canModify: boolean;
    canDelete: boolean;
} & CompanyScores;

export type CompanyScores = {
    scores?: Scores;

    scoresAvg?: Scores;
    scoresMax?: Scores;
    scoresMin?: Scores;
    scoresCount?: Scores;

    prevScores?: Scores;
    prevCampaignName?: string;
};
